/**
 * 쿠키 관리 유틸리티 함수들
 */

import { PasswordChangeType } from "@/utils/global-constants";
import { cryptoUtil } from "./crypto/crypto";

// 쿠키 키 상수
export const COOKIE_KEYS = {
  PASSWORD_CHANGE_REQUIRED: "password-change-required",
  PASSWORD_CHANGE_TYPE: "password-change-type",
  PASSWORD_CHANGE_COMPLETED: "password-change-completed",
  USER_ROLE: "user-role",
} as const;

/**
 * 쿠키 설정
 * @param key 쿠키 키
 * @param value 쿠키 값
 * @param maxAge 만료 시간 (초 단위)
 */
export const setCookie = (key: string, value: string, maxAge: number = 60 * 60 * 24): void => {
  if (typeof document !== "undefined") {
    document.cookie = `${key}=${value}; path=/; max-age=${maxAge}`;
  }
};

/**
 * 쿠키 삭제
 * @param key 쿠키 키
 */
export const deleteCookie = (key: string): void => {
  if (typeof document !== "undefined") {
    document.cookie = `${key}=; path=/; max-age=0`;
  }
};

/**
 * 패스워드 변경 필요 상태 설정
 * @param type 패스워드 변경 타입 (초기/만료)
 */
export const setPasswordChangeRequired = (type: PasswordChangeType): void => {
  setCookie(COOKIE_KEYS.PASSWORD_CHANGE_REQUIRED, "true");
  setCookie(COOKIE_KEYS.PASSWORD_CHANGE_TYPE, type);
  setCookie(COOKIE_KEYS.PASSWORD_CHANGE_COMPLETED, "false");
};

/**
 * 패스워드 변경 완료 상태 설정
 */
export const setPasswordChangeCompleted = (): void => {
  setCookie(COOKIE_KEYS.PASSWORD_CHANGE_COMPLETED, "true");
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_REQUIRED);
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_TYPE);
};

/**
 * 패스워드 변경 관련 쿠키 모두 삭제 (정상 로그인 시)
 */
export const clearPasswordChangeCookies = (): void => {
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_REQUIRED);
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_TYPE);
  deleteCookie(COOKIE_KEYS.PASSWORD_CHANGE_COMPLETED);
};

/**
 * 사용자 역할을 쿠키에 저장
 * @param role 사용자 역할
 */
export const setUserRoleCookie = async (role: string): Promise<void> => {
  console.log(`[setUserRoleCookie] :: setUserRoleCookie :: role:`, role);

  if (!role) {
    console.error(`[setUserRoleCookie] :: role이 비어있습니다.`);
    return;
  }

  const aesSecretKey = import.meta.env.VITE_SECRET_KEY;
  if (!aesSecretKey) {
    console.error(`[setUserRoleCookie] :: VITE_SECRET_KEY가 설정되지 않았습니다.`);
    return;
  }

  try {
    // 역할 값만 암호화 (SuperAdmin, Admin, Doctor)
    const encryptedRole = await cryptoUtil.encrypt(role, aesSecretKey);
    console.log(`[setUserRoleCookie] :: setUserRoleCookie :: encryptedRole:`, encryptedRole);

    // 쿠키에 암호화된 역할 값만 저장 (키 없이)
    setCookie(COOKIE_KEYS.USER_ROLE, encryptedRole);
  } catch (error) {
    console.error(`[setUserRoleCookie] :: 암호화 중 오류:`, error);
  }
};

/**
 * 쿠키에서 사용자 역할 조회
 * @returns 사용자 역할 또는 null
 */
export const getUserRoleFromCookie = async (): Promise<string | null> => {
  console.log(`[getUserRoleFromCookie] :: getUserRoleFromCookie`);
  if (typeof document === "undefined") return null;

  const cookies = document.cookie.split(";");
  const roleCookie = cookies.find((cookie) =>
    cookie.trim().startsWith(`${COOKIE_KEYS.USER_ROLE}=`)
  );

  console.log(`[getUserRoleFromCookie] :: getUserRoleFromCookie :: roleCookie:`, roleCookie);

  if (!roleCookie) return null;

  // user-role= 부분을 제거하고 암호화된 값만 추출
  // split("=") 대신 indexOf를 사용하여 첫 번째 = 이후의 모든 내용을 가져옴
  const equalIndex = roleCookie.indexOf("=");
  if (equalIndex === -1) {
    console.log(`[getUserRoleFromCookie] :: = 기호를 찾을 수 없습니다.`);
    return null;
  }

  const encryptedRole = roleCookie.substring(equalIndex + 1).trim();
  console.log(`[getUserRoleFromCookie] :: encryptedRole:`, encryptedRole);
  console.log(`[getUserRoleFromCookie] :: encryptedRole type:`, typeof encryptedRole);

  if (!encryptedRole) {
    console.log(`[getUserRoleFromCookie] :: 암호화된 역할 값이 비어있습니다.`);
    return null;
  }

  const aesSecretKey = import.meta.env.VITE_SECRET_KEY;
  if (!aesSecretKey) {
    console.error(`[getUserRoleFromCookie] :: VITE_SECRET_KEY가 설정되지 않았습니다.`);
    return null;
  }

  try {
    // 암호화된 값을 복호화하여 원본 역할 값 반환 (SuperAdmin, Admin, Doctor)
    const decryptedRole = await cryptoUtil.decrypt(encryptedRole, aesSecretKey);
    console.log(
      `[getUserRoleFromCookie] :: getUserRoleFromCookie :: decryptedRole:`,
      decryptedRole
    );
    return decryptedRole;
  } catch (error) {
    console.error(`[getUserRoleFromCookie] :: 복호화 중 오류:`, error);
    return null;
  }
};

/**
 * 사용자 역할 쿠키 삭제
 */
export const clearUserRoleCookie = (): void => {
  deleteCookie(COOKIE_KEYS.USER_ROLE);
};

/**
 * 쿠키에서 패스워드 변경 타입 조회
 * @returns 패스워드 변경 타입 또는 null
 */
export const getPasswordChangeType = (): PasswordChangeType | null => {
  if (typeof document === "undefined") return null;

  const cookies = document.cookie.split(";");
  const typeCookie = cookies.find((cookie) =>
    cookie.trim().startsWith(`${COOKIE_KEYS.PASSWORD_CHANGE_TYPE}=`)
  );

  if (!typeCookie) return null;

  const value = typeCookie.split("=")[1]?.trim();
  return value === PasswordChangeType.INITIAL || value === PasswordChangeType.EXPIRED
    ? (value as PasswordChangeType)
    : null;
};
